import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import CompanyForm from '@/components/CompanyForm';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { companyApi } from '@/lib/api';
import { Company, CompanyCreateRequest } from '@/types';

export default function CompanyPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if user has permission to manage company
  const canManageCompany = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';

  // Debug information
  console.log('Company page - User:', user);
  console.log('Company page - User role:', user?.role);
  console.log('Company page - Can manage company:', canManageCompany);

  useEffect(() => {
    // Temporarily allow all users for debugging
    // if (!canManageCompany) {
    //   navigate('/dashboard');
    //   return;
    // }
    fetchCompany();
  }, [canManageCompany, navigate]);

  const fetchCompany = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await companyApi.getCompany();
      
      if (response.success && response.data?.company) {
        setCompany(response.data.company);
        setIsEditing(false);
      } else {
        // No company exists yet, show form to create one
        setCompany(null);
        setIsEditing(true);
      }
    } catch (error: any) {
      console.error('Error fetching company:', error);
      if (error.response?.status === 404) {
        // Company not found, show form to create one
        setCompany(null);
        setIsEditing(true);
      } else {
        setError(error.response?.data?.error || 'Failed to fetch company details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData: CompanyCreateRequest) => {
    try {
      setSubmitting(true);
      setError(null);
      // console.log(formData,"0-0-0-0-0-0-0-0-0-0-0-0-")

      const response = await companyApi.saveCompany(formData);

      if (response.success && response.data?.company) {
        setCompany(response.data.company);
        setIsEditing(false);
        // You can add a success toast notification here
        console.log('Company saved successfully!');
      } else {
        throw new Error(response.error || 'Failed to save company');
      }
    } catch (error: any) {
      console.error('Error saving company:', error);
      setError(error.response?.data?.error || error.message || 'Failed to save company');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (company) {
      setIsEditing(false);
    } else {
      navigate('/dashboard');
    }
  };

  if (!canManageCompany) {
    // Temporarily show access denied message instead of redirecting
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-6 max-w-md">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            You need ADMIN or SUPERADMIN role to access company management.
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Current user: {user?.firstName} {user?.lastName}<br/>
            Current role: {user?.role}
          </p>
          <Button onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
     
        <div className="py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center items-center min-h-[400px]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading company details...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !isEditing) {
    return (
      <div className="min-h-screen bg-gray-50">
      
        <div className="py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="p-6 text-center">
              <div className="text-red-600 mb-4">
                <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Company</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <div className="space-x-4">
                <Button onClick={fetchCompany}>Try Again</Button>
                <Button variant="outline" onClick={() => navigate('/dashboard')}>
                  Back to Dashboard
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (isEditing || !company) {
    return (
      <div className="min-h-screen bg-gray-50">
       
        <div className="py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}
            
            <CompanyForm
              company={company || undefined}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              loading={submitting}
              isEdit={!!company}
            />
          </div>
        </div>
      </div>
    );
  }

  // Display company details
  return (
    <div className="min-h-screen bg-gray-50">
  
      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6 flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Company Profile</h1>
              <p className="text-gray-600 mt-2">Manage your company information and settings</p>
            </div>
            <Button onClick={() => setIsEditing(true)}>
              Edit Company
            </Button>
          </div>

          {/* Company Overview */}
          <Card className="p-6 mb-6">
            <div className="flex items-start space-x-6">
              {company.logo && (
                <img
                  src={company.logo}
                  alt={`${company.name} logo`}
                  className="w-20 h-20 object-contain rounded-lg border"
                />
              )}
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900">{company.name}</h2>
                {company.description && (
                  <p className="text-gray-700 mt-3">{company.description}</p>
                )}
                <div className="flex flex-wrap gap-4 mt-4 text-sm text-gray-600">
                  {company.website && (
                    <a
                      href={company.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {company.website}
                    </a>
                  )}
                  <span>Status: {company.isActive ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Locations */}
          {company.locations && company.locations.length > 0 ? (
            <Card className="p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Company Locations</h3>
                <span className="text-sm text-gray-500">
                  {company.locations.length} {company.locations.length === 1 ? 'Location' : 'Locations'}
                </span>
              </div>
              <div className="space-y-4">
                {company.locations.map((location, index) => (
                  <div 
                    key={location._id || index}
                    className={`border rounded-lg p-4 ${location.isMainOffice ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium text-gray-900">{location.name}</h4>
                          {location.isMainOffice && (
                            <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded">
                              Main Office
                            </span>
                          )}
                          <span className={`text-xs px-2 py-1 rounded ${location.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            {location.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <p>{location.address.street}</p>
                          <p>
                            {location.address.city}, {location.address.state} {location.address.zipCode}
                          </p>
                          <p>{location.address.country}</p>
                          {(location.contactInfo?.phone || location.contactInfo?.email) && (
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              {location.contactInfo.phone && (
                                <p>
                                  <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                  </svg>
                                  {location.contactInfo.phone}
                                </p>
                              )}
                              {location.contactInfo.email && (
                                <p>
                                  <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                  {location.contactInfo.email}
                                </p>
                              )}
                            </div>
                          )}
                          <p className="mt-2 text-xs text-gray-500">
                            <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {location.coordinates.latitude.toFixed(6)}, {location.coordinates.longitude.toFixed(6)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ) : (
            <Card className="p-6 mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Company Locations</h3>
              <div className="text-center py-8 text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p>No locations added yet.</p>
                <Button 
                  onClick={() => setIsEditing(true)}
                  className="mt-4"
                  variant="outline"
                >
                  Add Location Information
                </Button>
              </div>
            </Card>
          )}

          {/* Company Information */}
          <Card className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Company Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
              <div>
                <p><strong>Created:</strong> {new Date(company.createdAt).toLocaleDateString()}</p>
                <p><strong>Last Updated:</strong> {new Date(company.updatedAt).toLocaleDateString()}</p>
              </div>
              <div>
                <p><strong>Total Locations:</strong> {company.locations?.length || 0}</p>
                <p><strong>Main Office:</strong> {company.locations?.find(loc => loc.isMainOffice)?.name || 'Not Set'}</p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
